import { useEffect, useState, useContext } from "react";
import "./accountReferral.css";
import { CombinedContext } from "../../../context/combinedContext";
import billingRequest from "../../../request/billing";
import translations from "../../../translations/translations";
import { Gift, Copy, Share2 } from "lucide-react";
import AccountReferralSkeleton from "./accountReferralSkeleton/accountReferralSkeleton";

const REFERRAL_VALUE = 15;

const AccountReferral = () => {
    const { language, userData } = useContext(CombinedContext);

    const [referral, setReferral] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const t = translations[language].account.referral;

    useEffect(() => {
        if (!userData?.user?.userId) return;
        loadReferral();
    }, [userData]);

    const loadReferral = async () => {
        try {
            const res = await billingRequest.getReferralDashboard(
                userData.user.userId,
                userData.cognitoTokens.AccessToken
            );
            setReferral(res);
        } catch (err) {
            console.error("Failed to load referral dashboard:", err);
            setError(true);
        } finally {
            setLoading(false);
        }
    };
    
    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch (err) {
            console.error("Clipboard copy failed:", err);
        }
    };

    const generateCode = async () => {
        try {
            await billingRequest.generateReferralCode({
                userId: userData.user.userId,
                accessToken: userData.cognitoTokens.AccessToken,
            });
    
            loadReferral(); // refresh after
        } catch (err) {
            console.error("Failed to generate referral code:", err);
        }
    };

    if (loading) {
        return <AccountReferralSkeleton />;
    }

    if (error || !referral) {
        return <div className="sf-referral-loading">Unable to load referral information</div>;
    }

    const successfulReferrals = referral.referrals?.filter((r) => r.status === "active") || [];

    const totalSavings = successfulReferrals.length * REFERRAL_VALUE;

    const referralLink = `https://reservasinfila.com/listyourshop/${referral.referralCode}`;

    return (
        <div className="sf-referral-page">
            {/* HERO */}
            <div className="sf-referral-hero">
                <div className="sf-referral-hero-icon">
                    <Gift size={22} />
                </div>

                <h2 className="sf-referral-title">
                    {t.hero.title.replace("$15", "")}
                    <span>$15</span>
                </h2>

                <p className="sf-referral-subtitle">{t.hero.subtitle}</p>

                {totalSavings > 0 && <div className="sf-referral-earned">{t.hero.earned(totalSavings)}</div>}
            </div>

            {/* REFERRAL CODE */}
            <div className="sf-referral-card">
                <div className="sf-referral-label">{t.code.label}</div>

                {referral?.referralCode ? (
                    <>
                        <div className="sf-referral-box">
                            <span>{referral.referralCode}</span>
                            <button onClick={() => copyToClipboard(referral.referralCode)}>
                                <Copy size={16} />
                            </button>
                        </div>

                        <div className="sf-referral-label">{t.link.label}</div>

                        <div className="sf-referral-box">
                            <span>{referralLink}</span>
                            <button onClick={() => copyToClipboard(referralLink)}>
                                <Share2 size={16} />
                            </button>
                        </div>

                    </>
                ) : (
                    <>
                        <div className="sf-empty-small">
                            {translations[language].billing.referral.noCode}
                        </div>
                        <div className="sf-billing-referral-button-div">
                            <button
                                className="sf-btn sf-btn-primary sf-billing-referral-code-button"
                                onClick={generateCode}
                            >
                                {translations[language].billing.referral.generate}
                            </button>
                        </div>
                    </>
                )}

                <div className="sf-referral-hint">{t.hint}</div>
            </div>

            {/* HISTORY */}
            <div className="sf-referral-card">
                <h3 className="sf-referral-history-title">{t.history.title}</h3>

                {Array.isArray(referral.referrals) && referral.referrals.length > 0 ? (
                    referral.referrals.map((r, i) => (
                        <div key={i} className="sf-referral-history-item">
                            <span>{r.invitedShopName || "New shop"}</span>
                            <span className={`sf-badge sf-badge-${r.status || "pending"}`}>
                                {t.history[r.status] || t.history.pending}
                            </span>
                        </div>
                    ))
                ) : (
                    <div className="sf-referral-empty">{t.history.empty}</div>
                )}
            </div>
        </div>
    );
};

export default AccountReferral;
